import { Component, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { ElectionStatusEnum } from 'src/app/enums/ElectionStatusEnum';
import { PageTypeEnum } from 'src/app/enums/PageTypeEnum';
import { IReportTurnout } from 'src/app/interfaces/IReportTurnout';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { DistrictCount } from 'src/app/models/DistrictCount';
import { ElectionService } from 'src/app/services/election.service';

@Component({
  selector: 'app-district-essential-data-card',
  templateUrl: './district-essential-data-card.component.html',
  styleUrls: ['./district-essential-data-card.component.scss']
})
export class DistrictEssentialDataCardComponent implements AfterViewInit, OnDestroy {

  @Input() districtName: string = '';

  private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

  public district: DistrictCount | null = null;
  public turnout: IReportTurnout | null = null;
  public isFinal: boolean = false;

  constructor(public electionService: ElectionService) {
    this.electionService.dataLoaded$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(dataLoaded => {
        if (this.isDataLoaded(dataLoaded.electionDate)) {
          this.loadData(this.electionService.selectedPage);
        }
      });

    this.electionService.pageChanged$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(page => {
        this.loadData(page);
      });
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.loadData(this.electionService.selectedPage);
    }
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic
      ]);
    }
    return false;
  }

  private loadData(page: ISelectPage): void {
    const election = this.electionService.electionMap.get(page.electionDateString ?? '');
    if (!election) return;

    this.isFinal = election.haChange?.electionStatus === ElectionStatusEnum.final;

    if (page.pageType === PageTypeEnum.CountHADistrict) {
      this.district = election.electionCount?.getDistrict(page.countDistrict) ?? null;

      if (this.district) {
        this.turnout = {
          electorsEnrolled: this.district.districtEnrolled ?? 0,
          votesCast: this.district.districtTotalVotesCast ?? 0,
          votesCounted: this.district.districtTotalVotesCast ?? 0,
          formalVotes: this.district.districtTotalFormalVotes ?? 0,
          informalVotes: this.district.districtInformalVotes ?? 0,
          formalPercent: this.district.districtFormalVotesPercent ?? 0,
          informalPercent: this.district.districtInformalVotesPercent ?? 0,
          turnoutPercent: this.district.districtTurnoutPercent ?? 0,
          isProvisional: !this.isFinal,
          reportingRegion: this.district.districtName,
          informality: 0
        };
      }
    }
  }

  public openProfile(): void {
    if (this.district) {
      const url = `https://ecsa.sa.gov.au/electoral-districts/electoral-district-profiles/${this.district.districtName.toLowerCase()}`;
      window.open(url, '_blank')?.focus();
    }
  }

  /** Opens the district map in a new tab so it is more visible and can be downloaded. */
  public openMapInNewWindow(): void {
    if (this.district?.mapPath) {
      window.open(this.district.mapPath, '_blank', 'noopener,noreferrer')?.focus();
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
